import { useMemo } from "react";
import { navigate } from "../lib/router";
import { beachKey } from "../../shared/beaches";

// Multi-tile overview map showing every beach across every trip as a
// pin coloured per-trip. Dynamic zoom picks the largest z where the
// full bounding box of your beaches fits inside a 2×2 tile grid
// (single-tile at extreme zooms, world-scale for globe-spanning
// users). Same Web-Mercator projection the rest of the app uses; no
// map library.
//
// OSM tile terms apply — attribution baked into the corner; fine for
// a personal snorkel log, swap for a self-hosted or CDN'd source
// before public relaunch.

export interface OverviewPin {
  lat: number;
  lng: number;
  color: string;
  tripName: string;
  beachName: string;
}

interface View {
  z: number;
  tx0: number;
  ty0: number;
  cols: number;
  rows: number;
}

// Web Mercator lat/lng → world tile coordinate at zoom z. Same seam
// SpeciesDetail's world map uses, hoisted so we can share it between
// view computation and pin projection.
function project(lat: number, lng: number, z: number): {
  x: number;
  y: number;
} {
  const n = 2 ** z;
  const clampedLat = Math.max(-85.05, Math.min(85.05, lat));
  const x = ((lng + 180) / 360) * n;
  const latRad = (clampedLat * Math.PI) / 180;
  const y = ((1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2) * n;
  return { x, y };
}

function computeView(pins: OverviewPin[]): View | null {
  if (pins.length === 0) return null;
  const lats = pins.map((p) => p.lat);
  const lngs = pins.map((p) => p.lng);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const centerLat = (minLat + maxLat) / 2;
  const centerLng = (minLng + maxLng) / 2;
  // Pad the span a bit so pins don't sit right at the edge; degrees
  // work fine as a rough measure at these scales.
  const latSpan = Math.max(maxLat - minLat, 0.05) * 1.5;
  const lngSpan = Math.max(maxLng - minLng, 0.05) * 1.5;
  // 2 tiles wide at zoom z is 720/2^z degrees. Pick z such that the
  // wider span fits inside two tiles; clamp to reasonable extremes.
  const rawZ = Math.log2(720 / Math.max(latSpan, lngSpan));
  const z = Math.max(1, Math.min(15, Math.floor(rawZ)));

  const { x: cx, y: cy } = project(centerLat, centerLng, z);
  // A 2×2 grid centred on (cx, cy) sits at floor(cx - 1) top-left.
  return {
    z,
    tx0: Math.floor(cx - 1),
    ty0: Math.floor(cy - 1),
    cols: 2,
    rows: 2,
  };
}

function pinPos(pin: OverviewPin, view: View) {
  const { x, y } = project(pin.lat, pin.lng, view.z);
  const gx = x - view.tx0;
  const gy = y - view.ty0;
  return {
    left: (gx / view.cols) * 100,
    top: (gy / view.rows) * 100,
    visible: gx >= 0 && gx <= view.cols && gy >= 0 && gy <= view.rows,
  };
}

export function TripsOverviewMap({
  pins,
  interactive = true,
}: {
  pins: OverviewPin[];
  // When false (e.g. the public shared-trip page), pins are plain markers
  // that don't navigate to the planning route.
  interactive?: boolean;
}) {
  const view = useMemo(() => computeView(pins), [pins]);
  const tiles = useMemo(() => {
    if (!view) return [];
    const out: { z: number; x: number; y: number; col: number; row: number }[] =
      [];
    for (let r = 0; r < view.rows; r++) {
      for (let c = 0; c < view.cols; c++) {
        out.push({
          z: view.z,
          x: view.tx0 + c,
          y: view.ty0 + r,
          col: c,
          row: r,
        });
      }
    }
    return out;
  }, [view]);

  if (!view) return null;

  return (
    <div className="relative overflow-hidden rounded-xl bg-aqua/15 ring-1 ring-inset ring-ring">
      <div className="aspect-[3/2] w-full">
        <div
          className="grid h-full w-full"
          style={{
            gridTemplateColumns: `repeat(${view.cols}, 1fr)`,
            gridTemplateRows: `repeat(${view.rows}, 1fr)`,
          }}
        >
          {tiles.map((t) => (
            <img
              key={`${t.z}-${t.x}-${t.y}`}
              src={`https://tile.openstreetmap.org/${t.z}/${t.x}/${t.y}.png`}
              alt=""
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.visibility =
                  "hidden";
              }}
            />
          ))}
        </div>
        {pins.map((p, i) => {
          const pos = pinPos(p, view);
          if (!pos.visible) return null;
          const marker = (
            <svg viewBox="0 0 24 32" className="h-6 w-4 drop-shadow">
              <path
                d="M12 0C5.4 0 0 5.4 0 12c0 8 12 20 12 20s12-12 12-20C24 5.4 18.6 0 12 0z"
                fill={p.color}
              />
              <circle cx="12" cy="12" r="4" fill="#fff" />
            </svg>
          );
          const posStyle = { left: `${pos.left}%`, top: `${pos.top}%` };
          const key = `${p.tripName}-${p.beachName}-${i}`;
          // Non-interactive (share page): a plain, bottom-anchored marker.
          if (!interactive) {
            return (
              <span
                key={key}
                className="absolute z-10 flex h-11 w-11 -translate-x-1/2 -translate-y-full items-end justify-center"
                style={posStyle}
                title={p.beachName}
                aria-label={p.beachName}
              >
                {marker}
              </span>
            );
          }
          return (
            <button
              type="button"
              key={key}
              // 44×44 hit area (WCAG target size) with the pin bottom-
              // anchored: items-end + -translate-y-full keep the tip on the
              // coordinate while the surrounding box stays transparent, so
              // clustered pins are still individually tappable. See
              // trips-overview-map-pin-tap-target-16x24.
              className="absolute z-10 flex h-11 w-11 -translate-x-1/2 -translate-y-full items-end justify-center bg-transparent p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aqua-deep"
              style={posStyle}
              onClick={() =>
                navigate(
                  `/plan/${encodeURIComponent(beachKey(p.beachName))}`,
                )
              }
              aria-label={`Plan ${p.beachName} (${p.tripName})`}
              title={`${p.beachName} · ${p.tripName}`}
            >
              {marker}
            </button>
          );
        })}
      </div>
      <span className="absolute bottom-1 right-1 rounded bg-white/80 px-1.5 py-0.5 text-[9px] font-semibold text-ink/60">
        <a
          href="https://www.openstreetmap.org/copyright"
          target="_blank"
          rel="noopener noreferrer"
          className="text-ink/60 hover:underline"
        >
          © OpenStreetMap
        </a>
      </span>
    </div>
  );
}

// Trip-color palette. Colorblind-safe (Okabe-Ito ordering) so
// deuteranopia / protanopia users can still tell trips apart, and each
// hue reads with reasonable contrast on the light OSM base. Trip index
// → colour by modulo; more than 8 trips cycle.
export const TRIP_COLORS = [
  "#0072B2", // blue
  "#E69F00", // orange
  "#009E73", // bluish green
  "#CC79A7", // reddish purple
  "#56B4E9", // sky blue
  "#D55E00", // vermillion
  "#F0E442", // yellow (darker)
  "#000000", // black — highest contrast on a light basemap
];
